'use client';

import React, { useCallback, useRef, useState } from 'react';
import { useTranslations } from 'next-intl';
import { Upload, AlertTriangle, Loader2 } from 'lucide-react';
import { Button } from '@/shared/components/ui/button';
import { cn } from '@/shared/utils/cn';

const ACCEPTED_TYPES = new Set(['image/jpeg', 'image/png']);
const MAX_SIZE_BYTES = 10 * 1024 * 1024; // 10 MB

interface ImageUploadPanelProps {
  onImageSelected: (file: File) => void;
  isProcessing: boolean;
  error: string | null;
  onRetry: () => void;
}

export function ImageUploadPanel({
  onImageSelected,
  isProcessing,
  error,
  onRetry,
}: ImageUploadPanelProps) {
  const t = useTranslations();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragOver, setIsDragOver] = useState(false);

  const validateAndSelect = useCallback(
    (file: File) => {
      if (!ACCEPTED_TYPES.has(file.type)) {
        return;
      }
      if (file.size > MAX_SIZE_BYTES) {
        return;
      }
      onImageSelected(file);
    },
    [onImageSelected]
  );

  const handleFileChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) {
        validateAndSelect(file);
      }
      // Reset input so re-selecting the same file triggers onChange
      e.target.value = '';
    },
    [validateAndSelect]
  );

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragOver(false);
      const file = e.dataTransfer.files[0];
      if (file) {
        validateAndSelect(file);
      }
    },
    [validateAndSelect]
  );

  const handleUploadClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  if (isProcessing) {
    return (
      <div
        data-testid="maze-upload-area"
        className="flex flex-col items-center justify-center gap-3 rounded-lg border-2 border-dashed border-muted-foreground/25 bg-muted/30 p-8 text-center"
      >
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <p className="text-sm font-medium">{t('games.maze.processing')}</p>
        <p className="text-xs text-muted-foreground">
          {t('games.maze.removeBackground')}
        </p>
        <div className="h-1.5 w-48 overflow-hidden rounded-full bg-muted">
          <div className="h-full animate-pulse rounded-full bg-primary/60" style={{ width: '60%' }} />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div
        data-testid="maze-upload-area"
        className="flex flex-col items-center justify-center gap-3 rounded-lg border-2 border-dashed border-destructive/40 bg-destructive/5 p-8 text-center"
      >
        <AlertTriangle className="h-8 w-8 text-destructive" />
        <p className="text-sm text-destructive">{error}</p>
        <Button
          variant="outline"
          size="sm"
          onClick={onRetry}
          data-testid="maze-upload-btn"
        >
          {t('games.maze.tryAnother')}
        </Button>
      </div>
    );
  }

  return (
    <div
      data-testid="maze-upload-area"
      className={cn(
        'flex flex-col items-center justify-center gap-3 rounded-lg border-2 border-dashed p-8 text-center transition-colors cursor-pointer',
        isDragOver
          ? 'border-primary bg-primary/10'
          : 'border-muted-foreground/25 bg-muted/30 hover:border-primary/50 hover:bg-muted/50'
      )}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      onClick={handleUploadClick}
    >
      <Upload className="h-8 w-8 text-muted-foreground" />
      <p className="text-sm text-muted-foreground">
        {t('games.maze.uploadHint')}
      </p>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png"
        className="hidden"
        onChange={handleFileChange}
        data-testid="maze-file-input"
      />
      <Button
        type="button"
        variant="default"
        size="sm"
        onClick={(e) => {
          e.stopPropagation();
          handleUploadClick();
        }}
        data-testid="maze-upload-btn"
      >
        {t('games.maze.uploadImage')}
      </Button>
      <p className="text-xs text-muted-foreground">
        {t('games.maze.fileConstraint')}
      </p>
    </div>
  );
}
